/**
 * Demo car store — a 1:1 port of the mobile app's `seedDemoData` (a Porsche 911
 * GTS with fuel history, a few spendings, a trip and a preferred service), so a
 * visitor can explore the web app without importing their own backup.
 *
 * Values that are randomised on the phone (per-fill volume/price/distance) are
 * pinned here to realistic constants so the demo is stable across reloads. The
 * fuel→spend "mirror" is intentionally NOT added to `spendings` — the web ledger
 * derives the Fuel category from `fuelLogs` (see calc/ledger.ts), and storing the
 * mirror would double-count.
 */
import { CURRENT_VERSION, type CanonicalStore } from "./model.ts";

const DAY = 86400; // seconds

export function buildDemoStore(): CanonicalStore {
  const nowMs = Date.now();
  const now = Math.floor(nowMs / 1000);
  const carId = "demo-gt3";

  // --- Car (matches mobile seedDemoData) -----------------------------------
  const tankVolume = 64;
  const fullRange = 516;
  const rangeLeft = 266; // ~250 km used from a full tank
  const percent = (rangeLeft / fullRange) * 100;
  const volumeLeft = (percent / 100) * tankVolume;

  const cars = [{
    id: carId,
    make: "Porsche",
    model: "(DEMO) 911 GTS",
    year: 2024,
    fuelType: "Petrol",
    tankVolume,
    manufacturerAvgConsumption: 12.4,
    licensePlate: "P 911 GTS",
    color: "Guards Red",
    tankEstimate: { fullRange, rangeLeft, percent, volume: volumeLeft, savedAt: now },
    purchasePrice: 182900,
    purchaseCurrency: "USD",
    purchaseDate: now - DAY * 365,          // 1 year ago
    insuranceAmount: 1200,
    insuranceExpiry: now + DAY * 180,       // 6 months out
    roadTaxAmount: 450,
    roadTaxExpiry: now + DAY * 300,         // ~10 months out
    createdAt: now - DAY * 365,
  }];

  // --- Fuel logs (10 entries, Jan 2026 → Jul 2026) -------------------------
  const stations = ["Shell Pitstop", "Aral Fastlane", "BP Premium", "Total Energies"];
  const grades = ["Premium 98", "Regular 95", "Super 98"];
  const basePrice = 1.42;
  // Pinned per-fill variation (phone randomises these).
  const priceVar = [0.03, -0.02, 0.05, -0.04, 0.01, 0.04, -0.03, 0.02, -0.05, 0.03];
  const volumes = [44, 47, 41, 49, 43, 46, 40, 48, 42, 45];
  const distances = [420, 610, 530, 700, 480, 560, 390, 640, 450, 590];
  const speeds = [58, 62, 71, 49, 66, 54, 73, 60, 52, 68];
  const daysBetween = [18, 26, 15, 29, 21, 17, 24, 16, 28, 20];

  let odo = 5000;
  let date = Math.floor(Date.UTC(2026, 0, 5, 10, 30) / 1000); // Jan 5, 2026 10:30
  const fuelLogs = [];
  for (let i = 0; i < 10; i++) {
    odo += distances[i]!;
    const unitPrice = basePrice + priceVar[i]!;
    const volume = volumes[i]!;
    const cost = Math.round(volume * unitPrice * 100) / 100;
    const station = stations[i % stations.length]!;
    const grade = grades[i % grades.length]!;
    fuelLogs.push({
      id: `demo-fuel-${i + 1}`,
      carId,
      date,
      odometer: odo,
      volumeAdded: volume,
      totalCost: cost,
      currency: "USD",
      fuelGrade: grade,
      isFullFill: true,
      stationName: station,
      avgSpeed: speeds[i]!,
      notes: `Fill-up at ${station}. Car running smooth.`,
    });
    date += daysBetween[i]! * DAY;
  }

  // --- Manual spendings (the 3 "other" entries from mobile) ----------------
  const spendings = [
    { id: "demo-spend-1", carId, amount: 75, category: "Other", date: now - DAY * 20, currency: "USD", notes: "Toll fees Munich–Salzburg" },
    { id: "demo-spend-2", carId, amount: 180, category: "Parking", date: now - DAY * 12, currency: "USD", notes: "Parking pass — monthly subscription" },
    { id: "demo-spend-3", carId, amount: 42.5, category: "General Maintenance", date: now - DAY * 5, currency: "USD", notes: "Premium hand wash & interior detail" },
  ];

  // --- Trip with 2 waypoints ----------------------------------------------
  const trips = [{
    id: "demo-trip-1",
    carId,
    date: now - DAY * 3,
    startOdometer: 9800,
    endOdometer: 10150,
    startLocationName: "Stuttgart",
    endLocationName: "Nürburgring",
    waypoints: ["Heidelberg", "Frankfurt"],
    purpose: "Personal",
    notes: "Weekend track day trip.",
    fuelCost: 85,
  }];

  // --- Preferred service ---------------------------------------------------
  const services = [{
    id: "demo-svc-1",
    name: "Porsche Center Stuttgart",
    category: "Dealer",
    address: "Porscheplatz 1, 70435 Stuttgart, Germany",
    email: "service@porsche-stuttgart.de",
    phone: "+49 711 911-0",
    notes: "Main dealer for official maintenance.",
    createdAt: now,
  }];

  return {
    schemaVersion: CURRENT_VERSION,
    profile: { displayCurrency: "USD", unitSystem: "metric" },
    cars,
    fuelLogs,
    spendings,
    tyres: [],
    tyreSets: [],
    trips,
    maintenanceReminders: [],
    services,
    skipped: [],
  };
}
